import { useEffect, useRef } from 'react';
import { EditorView, keymap, lineNumbers, highlightActiveLineGutter } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { css } from '@codemirror/lang-css';
import { indentWithTab } from '@codemirror/commands';
import type { ViewUpdate } from '@codemirror/view';
import { defaultHighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { getDarkTheme, getLightTheme, themeCompartment } from './CodeMirrorTheme';
import './CodeMirrorCssEditor.css';

interface CodeMirrorCssEditorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  minHeight?: string;
}

export const CodeMirrorCssEditor = ({
  value,
  onChange,
  className = '',
  minHeight = '300px',
}: CodeMirrorCssEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const initializedRef = useRef(false);

  // Detect current theme from document
  const isDarkMode = (): boolean => {
    const htmlElement = document.documentElement;
    return htmlElement.getAttribute('data-theme') === 'dark' ||
           htmlElement.classList.contains('dark') ||
           window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  // Create editor on mount
  useEffect(() => {
    if (!editorRef.current || viewRef.current || initializedRef.current) return;

    const darkMode = isDarkMode();
    const themeExtension = darkMode ? getDarkTheme() : getLightTheme();

    const startState = EditorState.create({
      doc: value,
      extensions: [
        themeExtension,
        lineNumbers(),
        highlightActiveLineGutter(),
        syntaxHighlighting(defaultHighlightStyle),
        css(), // CSS language support
        keymap.of([indentWithTab]), // Tab key for indentation
        EditorView.updateListener.of((update: ViewUpdate) => {
          if (update.docChanged) {
            onChange(update.state.doc.toString());
          }
        }),
        EditorView.lineWrapping,
        EditorState.tabSize.of(2),
      ],
    });

    const view = new EditorView({
      state: startState,
      parent: editorRef.current,
    });

    viewRef.current = view;
    initializedRef.current = true;

    // Listen for theme changes
    const handleThemeChange = () => {
      const newDarkMode = isDarkMode();
      const newTheme = newDarkMode ? getDarkTheme() : getLightTheme();
      if (viewRef.current) {
        viewRef.current.dispatch({
          effects: themeCompartment.reconfigure(newTheme),
        });
      }
    };

    // Watch for changes to data-theme attribute
    const observer = new MutationObserver(handleThemeChange);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Update content when value prop changes externally (from parent)
  useEffect(() => {
    if (!viewRef.current) return;

    const currentDoc = viewRef.current.state.doc.toString();
    if (value !== currentDoc) {
      const transaction = viewRef.current.state.update({
        changes: { from: 0, to: currentDoc.length, insert: value },
      });
      viewRef.current.dispatch(transaction);
    }
  }, [value]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
        initializedRef.current = false;
      }
    };
  }, []);

  return (
    <div
      className={`codemirror-css-editor ${className}`}
      style={{ minHeight, height: '100%' }}
    >
      <div ref={editorRef} />
    </div>
  );
};
